import { useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import useExtensionConnection from './use-extension-connection'
import { ExtensionConnectionTypes } from '../../types'

interface ReturnType {
  isConnectionConfirmed: boolean
  isLoading: boolean
  hasError: boolean
  errorMessageKey: string
}

const CONNECTION_AVAILABLE = 26106
const INAKTIVE_TARIFF = 26105
const EHA_ERROR = 26111
const LINKING_TARIFF_ERROR = 26109

/**
 * This hook confirm a extension connection if the user is redirected from the vendor partner.
 * It will also handle the error messages.
 */
const useExtensionConfirmation = (extensionName: ExtensionConnectionTypes): ReturnType => {
  const { installationId } = useParams<{ installationId: string }>()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const vendorAccountId = queryParams.get('vendorAccount') ?? undefined
  const generalErrorKey = `installation-extensions.${extensionName}-confirmations.error.general`
  const alreadyConnectedErrorKey = `installation-extensions.${extensionName}-confirmations.error.already-connected`
  const inactiveTariffErrorKey = `installation-extensions.${extensionName}-confirmations.error.inactive-tariff`
  const ehaErrorKey = `installation-extensions.${extensionName}-confirmations.error.eha`
  const linkingErrorKey = `installation-extensions.${extensionName}-confirmations.error.linking-tariff`

  const { connectionState, handleExtensionConnectionConfirmation: handleVendorConnectionConfirmation } = useExtensionConnection(extensionName)

  useEffect(() => {
    handleVendorConnectionConfirmation(installationId, vendorAccountId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { isLoading, isConnectionConfirmed, hasError, errorNumber } = connectionState

  const getErrorMessageKey = (errorNumber?: number): string => {
    if (errorNumber === CONNECTION_AVAILABLE) return alreadyConnectedErrorKey
    if (errorNumber === INAKTIVE_TARIFF) return inactiveTariffErrorKey
    if (errorNumber === EHA_ERROR) return ehaErrorKey
    if (errorNumber === LINKING_TARIFF_ERROR) return linkingErrorKey
    return generalErrorKey
  }
  const errorMessageKey = getErrorMessageKey(errorNumber)

  return { isLoading, isConnectionConfirmed, hasError, errorMessageKey }
}

export default useExtensionConfirmation
